import React from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import axios from "~/config/axios";
import {
  CheckoutTermPopupView,
  LoadMoreDatesArgs
} from "./CheckoutTermPopup.view";
import { createFreePurchase } from "~/lib/createFreePurchase";
import { createCheckoutSession } from "~/lib/createCheckoutSession";
import { login } from "~/lib/login";
import { validateDiscount } from "~/lib/validateDiscount";
import { useStore } from "~/hooks/useStore";

export function CheckoutTermPopup({
  course,
  earlyAccess,
  locations,
  onClose,
  onJoinWaitingList,
  onViewTrial,
  tutors,
  ticketTypes
}: Props) {
  const [{ user }] = useStore("authentication");

  const { executeRecaptcha } = useGoogleReCaptcha();

  async function onCheckoutLogin() {
    return await login();
  }

  function onViewTrialClick() {
    onClose && onClose();
    onViewTrial && onViewTrial();
  }

  async function onGetPaymentIntent(data: any) {
    try {
      if (!executeRecaptcha) throw "Execute recaptcha not yet available";

      const recaptchaToken = await executeRecaptcha(
        "MarketplaceRegularCHECKOUT"
      );
      return await createCheckoutSession({
        ...data,
        recaptchaToken
      });
    } catch (e) {
      throw (e as any).response?.data?.message ??
        (e as any).message ??
        "Something went wrong";
    }
  }

  async function onConfirmFreeBooking(data: any) {
    if (!executeRecaptcha) throw "Execute recaptcha not yet available";

    const recaptchaToken = await executeRecaptcha(
      "MarketplaceRegularCHECKOUTFree"
    );

    let checkoutData = { ...data, recaptchaToken: recaptchaToken };
    await createFreePurchase(checkoutData, onPurchase);
  }

  async function onLoadMoreDates({ page, limit }: LoadMoreDatesArgs) {
    const response = await axios.get(`courses/${course._id}/term-dates`, {
      params: { page, limit, earlyAccess }
    });
    return response.data;
  }

  function onPurchase(purchase: any, price = 0) {
    if ((window as any).dataLayer) {
      (window as any).dataLayer.push({
        event: "Order Completed",
        event_category: "purchase",
        event_label: "purchase",
        value: price / 100,
        order_id: purchase._id,
        email: purchase.bookingData.user.renderEmail,
        total: price / 100,
        currency: "GBP",
        products: [
          {
            course_id: purchase.bookingData.courseId
          }
        ]
      });
    }
  }

  async function onValidateDiscount(code: string, email: string) {
    return await validateDiscount(code, email, course._id, course.schoolId);
  }

  return (
    <CheckoutTermPopupView
      course={course}
      locations={locations}
      onClose={onClose}
      onCheckoutLogin={onCheckoutLogin}
      onConfirmFreeBooking={onConfirmFreeBooking}
      onGetPaymentIntent={onGetPaymentIntent}
      onJoinWaitingList={onJoinWaitingList}
      onLoadMoreDates={onLoadMoreDates}
      onValidateDiscount={onValidateDiscount}
      onViewTrialClick={onViewTrialClick}
      ticketTypes={ticketTypes}
      tutors={tutors}
      user={user}
    />
  );
}

interface Props {
  course: any;
  earlyAccess?: string;
  locations: any[];
  onClose: () => void;
  onJoinWaitingList: () => void;
  onViewTrial: () => void;
  session?: any;
  ticketTypes: any[];
  tutors: any[];
}
